import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { actionGetRecruiment } from "../pages/desktop/chatbot-recruiment/action";
import { buildCampaignListParams, extractCampaignList, FILTER_ALL, findCampaignById, normalizeCampaign } from "../utils/constants/config";
import {
  saveCandidateToStorage,
  loadCandidateFromStorage,
  hasCandidateInfo,
  loadOpenChatStateFromStorage,
  saveOpenChatStateToStorage,
  clearOpenChatStateFromStorage,
} from "../utils/helps";

const RecruitmentContext = createContext(null);

export const RecruitmentProvider = ({ children }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchName, setSearchName] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [experienceFilter, setExperienceFilter] = useState(FILTER_ALL);
  const [salaryFilter, setSalaryFilter] = useState(FILTER_ALL);
  const [educationFilter, setEducationFilter] = useState(FILTER_ALL);
  const [candidateInfo, setCandidateInfo] = useState(() => loadCandidateFromStorage());
  const initialOpenChat = loadOpenChatStateFromStorage();
  const [chatStarted, setChatStarted] = useState(
    () =>
      hasCandidateInfo(loadCandidateFromStorage()) ||
      initialOpenChat.openChatCampaignIds.length > 0,
  );
  const [candidateModalOpen, setCandidateModalOpen] = useState(false);
  const [candidateModalShowCv, setCandidateModalShowCv] = useState(false);
  const [modalCampaignId, setModalCampaignId] = useState(null);
  const [messagesByCampaign, setMessagesByCampaign] = useState({});
  const [openChatCampaignIds, setOpenChatCampaignIds] = useState(
    initialOpenChat.openChatCampaignIds,
  );
  const [activeChatCampaignId, setActiveChatCampaignId] = useState(
    initialOpenChat.activeChatCampaignId ??
    initialOpenChat.openChatCampaignIds.at(-1) ??
    null,
  );
  const [campaignCache, setCampaignCache] = useState({});

  const isCandidateRegistered = useMemo(
    () => hasCandidateInfo(candidateInfo),
    [candidateInfo],
  );

  const campaigns = useMemo(() => data.map(normalizeCampaign), [data]);

  useEffect(() => {
    if (campaigns.length === 0) return;

    setCampaignCache((prev) => {
      const next = { ...prev };
      let changed = false;

      campaigns.forEach((item) => {
        const key = String(item.id);
        if (next[key] !== item) {
          next[key] = item;
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [campaigns]);

  const getCampaignById = useCallback(
    (id) => {
      if (id == null || id === "") return undefined;
      return findCampaignById(campaigns, id) ?? campaignCache[String(id)];
    },
    [campaigns, campaignCache],
  );

  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildCampaignListParams({
        name: searchName,
        experience: experienceFilter,
        salary: salaryFilter,
        education: educationFilter,
      });
      const response = await actionGetRecruiment(params);
      if (response?.status === 200) {
        setData(extractCampaignList(response.data));
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }, [searchName, experienceFilter, salaryFilter, educationFilter]);

  useEffect(() => {
    const delay = searchName.trim() ? 400 : 0;
    const timeoutId = setTimeout(() => {
      fetchCampaigns();
    }, delay);

    return () => clearTimeout(timeoutId);
  }, [fetchCampaigns, searchName]);

  useEffect(() => {
    if (!searchInput.trim()) {
      setSearchName((prev) => (prev ? "" : prev));
    }
  }, [searchInput]);

  useEffect(() => {
    if (
      !hasCandidateInfo(candidateInfo) &&
      openChatCampaignIds.length === 0
    ) {
      clearOpenChatStateFromStorage();
      return;
    }
    saveOpenChatStateToStorage(openChatCampaignIds, activeChatCampaignId);
  }, [openChatCampaignIds, activeChatCampaignId, candidateInfo]);

  // bỏ các tab khi tìm kiếm trong chi tiết (chỉ khi đang lọc theo tên)
  useEffect(() => {
    if (loading || !searchName.trim()) return;

    const validIds = new Set(campaigns.map((c) => String(c.id)));

    setOpenChatCampaignIds((prev) => {
      const next = prev.filter((id) => validIds.has(String(id)));
      if (next.length === prev.length) return prev;

      setActiveChatCampaignId((current) => {
        if (current != null && validIds.has(String(current))) return current;
        return next[next.length - 1] ?? null;
      });

      return next;
    });
  }, [campaigns, loading, searchName]);

  const openCandidateModal = useCallback((campaignId = null, options = {}) => {
    if (campaignId != null) {
      setModalCampaignId(String(campaignId));
    }
    setCandidateModalShowCv(Boolean(options.showCvUpload));
    setCandidateModalOpen(true);
  }, []);

  const openChatCampaign = useCallback((campaignId) => {
    if (campaignId == null || campaignId === "") return null;

    const id = String(campaignId);

    setOpenChatCampaignIds((prev) =>
      prev.includes(id) ? prev : [...prev, id],
    );
    setActiveChatCampaignId(id);
    setChatStarted(true);

    return id;
  }, []);

  const openGeneralChat = useCallback(() => {
    setActiveChatCampaignId(null);
    setChatStarted(true);
  }, []);

  const closeChatCampaign = useCallback((campaignId) => {
    if (campaignId == null || campaignId === "") return null;

    const id = String(campaignId);
    let nextActiveId = null;

    setOpenChatCampaignIds((prev) => {
      const next = prev.filter((item) => item !== id);
      nextActiveId = next[next.length - 1] ?? null;
      return next;
    });

    setActiveChatCampaignId((current) => {
      if (current !== id) return current;
      return nextActiveId;
    });

    return nextActiveId;
  }, []);

  const activateChatCampaign = useCallback((campaignId) => {
    if (campaignId == null || campaignId === "") return null;

    const id = String(campaignId);
    setOpenChatCampaignIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
    setActiveChatCampaignId(id);

    return id;
  }, []);

  const closeCandidateModal = useCallback(() => {
    setCandidateModalOpen(false);
    setCandidateModalShowCv(false);
  }, []);

  const saveCandidateInfo = useCallback((info, options) => {
    setCandidateInfo(info);
    saveCandidateToStorage(info, options);
  }, []);

  const getMessagesForCampaign = useCallback(
    (campaignId) => messagesByCampaign[String(campaignId)] ?? [],
    [messagesByCampaign],
  );

  const setMessagesForCampaign = useCallback((campaignId, updater) => {
    const key = String(campaignId);
    setMessagesByCampaign((prev) => ({
      ...prev,
      [key]: typeof updater === "function" ? updater(prev[key] ?? []) : updater,
    }));
  }, []);

  const startChatSession = useCallback(
    (values) => {
      const info = {
        fullName: values.fullName?.trim() || "",
        email: values.email?.trim() || "",
        phone: values.phone?.trim() || "",
        consent: Boolean(values.consent),
      };
      saveCandidateInfo(info, { resetTimestamp: true });
      setChatStarted(true);
      setCandidateModalOpen(false);
      setCandidateModalShowCv(false);
    },
    [saveCandidateInfo],
  );

  useEffect(() => {
    const syncExpiredCandidate = () => {
      const fresh = loadCandidateFromStorage();
      if (!hasCandidateInfo(fresh)) {
        setCandidateInfo(fresh);
        setChatStarted(false);
        setOpenChatCampaignIds([]);
        setActiveChatCampaignId(null);
        clearOpenChatStateFromStorage();
      }
    };

    syncExpiredCandidate();
    document.addEventListener("visibilitychange", syncExpiredCandidate);
    return () =>
      document.removeEventListener("visibilitychange", syncExpiredCandidate);
  }, []);

  const requestChatAccess = useCallback(
    (campaignId = null) => {
      if (isCandidateRegistered) {
        if (campaignId != null && campaignId !== "") {
          openChatCampaign(campaignId);
        } else {
          openGeneralChat();
        }
        return true;
      }
      openCandidateModal(campaignId);
      return false;
    },
    [isCandidateRegistered, openCandidateModal, openChatCampaign, openGeneralChat],
  );

  const handleSearch = () => {
    setSearchName(searchInput);
  };

  const clearSearch = useCallback(() => {
    setSearchInput("");
    setSearchName("");
  }, []);

  const selectChatCampaign = useCallback(
    (campaignId) => {
      if (campaignId == null || campaignId === "") return false;

      if (!isCandidateRegistered) {
        openCandidateModal(campaignId);
        return false;
      }

      setSearchInput("");
      setSearchName("");
      openChatCampaign(campaignId);
      return true;
    },
    [isCandidateRegistered, openCandidateModal, openChatCampaign],
  );

  const enterCampaignFromList = useCallback(
    (campaignId) => {
      if (campaignId == null || campaignId === "") return false;

      const id = String(campaignId);
      setSearchInput("");
      setSearchName("");

      if (isCandidateRegistered) {
        setOpenChatCampaignIds([id]);
        setActiveChatCampaignId(id);
        setChatStarted(true);
      } else {
        setOpenChatCampaignIds([]);
        setActiveChatCampaignId(null);
      }

      return true;
    },
    [isCandidateRegistered],
  );

  return (
    <RecruitmentContext.Provider
      value={{
        campaigns,
        getCampaignById,
        loading,
        searchInput,
        setSearchInput,
        searchName,
        setSearchName,
        handleSearch,
        clearSearch,
        selectChatCampaign,
        enterCampaignFromList,
        experienceFilter,
        setExperienceFilter,
        salaryFilter,
        setSalaryFilter,
        educationFilter,
        setEducationFilter,
        refetchCampaigns: fetchCampaigns,
        candidateInfo,
        setCandidateInfo: saveCandidateInfo,
        isCandidateRegistered,
        chatStarted,
        setChatStarted,
        openChatCampaignIds,
        activeChatCampaignId,
        openChatCampaign,
        openGeneralChat,
        closeChatCampaign,
        activateChatCampaign,
        candidateModalOpen,
        candidateModalShowCv,
        modalCampaignId,
        openCandidateModal,
        closeCandidateModal,
        startChatSession,
        requestChatAccess,
        getMessagesForCampaign,
        setMessagesForCampaign,
        messagesByCampaign,
      }}
    >
      {children}
    </RecruitmentContext.Provider>
  );
};

export const useRecruitment = () => {
  const context = useContext(RecruitmentContext);
  if (!context) {
    throw new Error("useRecruitment must be used within RecruitmentProvider");
  }
  return context;
};
